import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { useEffect } from "react";
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  ListBullets, ListNumbers,
  TextAlignLeft, TextAlignCenter, TextAlignRight, TextAlignJustify,
  TextHOne, TextHTwo, TextHThree, LinkSimple, LinkBreak,
  ArrowUUpLeft, ArrowUUpRight, Paragraph as ParaIcon, Minus, Table as TableIcon,
  RowsPlusTop, RowsPlusBottom, ColumnsPlusLeft, ColumnsPlusRight,
  Rows, Columns, X,
} from "@phosphor-icons/react";

/**
 * Microsoft Word-style rich text editor built on TipTap.
 * Value/onChange work with HTML strings.
 * Optional `placeholders`: [{ token, label }] — renders one-click chips that
 * insert the token at the current cursor position (e.g. {{COMPANY_NAME}}).
 */
export default function RichTextEditor({ value = "", onChange, placeholder, testid, minHeight = 320, placeholders }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false, autolink: true }),
      Table.configure({ resizable: true, HTMLAttributes: { class: "rte-table" } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-6 py-5 min-h-full",
        "data-testid": testid ? `${testid}-editor` : "rich-text-editor",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value → editor (e.g. template picker fill)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming !== current && incoming !== (current === "<p></p>" ? "" : current)) {
      editor.commands.setContent(incoming || "<p></p>", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  return (
    <div className="border border-border rounded-sm bg-white" data-testid={testid}>
      <Toolbar editor={editor} placeholders={placeholders} />
      <div style={{ minHeight }} className="bg-white">
        <EditorContent editor={editor} />
      </div>
      {placeholder && editor.isEmpty && (
        <div className="pointer-events-none -mt-[280px] px-6 pt-0 text-sm text-muted-foreground/60 italic">
          {placeholder}
        </div>
      )}
    </div>
  );
}

function Toolbar({ editor, placeholders }) {
  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertToken = (token) => {
    editor.chain().focus().insertContent(token).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-[#F4F4F4] sticky top-0 z-10">
      <Group>
        <TbBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid="rte-undo">
          <ArrowUUpLeft size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid="rte-redo">
          <ArrowUUpRight size={16} weight="bold" />
        </TbBtn>
      </Group>

      <Sep />

      <Group>
        <StyleSelect editor={editor} />
      </Group>

      <Sep />

      <Group>
        <TbBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (Ctrl+B)" testid="rte-bold">
          <TextB size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (Ctrl+I)" testid="rte-italic">
          <TextItalic size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline (Ctrl+U)" testid="rte-underline">
          <TextUnderline size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough" testid="rte-strike">
          <TextStrikethrough size={16} weight="bold" />
        </TbBtn>
      </Group>

      <Sep />

      <Group>
        <TbBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left" testid="rte-align-left">
          <TextAlignLeft size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center" testid="rte-align-center">
          <TextAlignCenter size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right" testid="rte-align-right">
          <TextAlignRight size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justify" testid="rte-align-justify">
          <TextAlignJustify size={16} weight="bold" />
        </TbBtn>
      </Group>

      <Sep />

      <Group>
        <TbBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list" testid="rte-bullet-list">
          <ListBullets size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testid="rte-ordered-list">
          <ListNumbers size={16} weight="bold" />
        </TbBtn>
      </Group>

      <Sep />

      <Group>
        <TbBtn onClick={setLink} active={editor.isActive("link")} title="Insert link" testid="rte-link">
          <LinkSimple size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive("link")} title="Remove link" testid="rte-unlink">
          <LinkBreak size={16} weight="bold" />
        </TbBtn>
        <TbBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Insert horizontal line" testid="rte-hr">
          <Minus size={16} weight="bold" />
        </TbBtn>
        <TbBtn
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          title="Insert table"
          testid="rte-table"
        >
          <TableIcon size={16} weight="bold" />
        </TbBtn>
      </Group>

      {editor.isActive("table") && (
        <>
          <Sep />
          <Group>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground pl-1 pr-1">Table:</span>
            <TbBtn onClick={() => editor.chain().focus().addRowBefore().run()} title="Insert row above" testid="rte-row-above">
              <RowsPlusTop size={16} weight="bold" />
            </TbBtn>
            <TbBtn onClick={() => editor.chain().focus().addRowAfter().run()} title="Insert row below" testid="rte-row-below">
              <RowsPlusBottom size={16} weight="bold" />
            </TbBtn>
            <TbBtn onClick={() => editor.chain().focus().deleteRow().run()} title="Delete row" testid="rte-row-del">
              <Rows size={16} weight="bold" />
            </TbBtn>
            <TbBtn onClick={() => editor.chain().focus().addColumnBefore().run()} title="Insert column left" testid="rte-col-left">
              <ColumnsPlusLeft size={16} weight="bold" />
            </TbBtn>
            <TbBtn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Insert column right" testid="rte-col-right">
              <ColumnsPlusRight size={16} weight="bold" />
            </TbBtn>
            <TbBtn onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete column" testid="rte-col-del">
              <Columns size={16} weight="bold" />
            </TbBtn>
            <TbBtn onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table" testid="rte-table-del">
              <X size={16} weight="bold" />
            </TbBtn>
          </Group>
        </>
      )}

      {placeholders && placeholders.length > 0 && (
        <>
          <Sep />
          <Group>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground pl-1 pr-1">Insert:</span>
            {placeholders.map(p => (
              <button
                key={p.token}
                type="button"
                onClick={() => insertToken(p.token)}
                title={`Insert ${p.token}`}
                data-testid={`rte-insert-${p.token.replace(/[^A-Za-z0-9]+/g, "").toLowerCase()}`}
                className="h-7 px-2 rounded-sm border border-border bg-white hover:border-[#0F62FE] hover:text-[#0F62FE] font-mono text-[11px] uppercase tracking-wider text-foreground"
              >
                {p.label}
              </button>
            ))}
          </Group>
        </>
      )}
    </div>
  );
}

function StyleSelect({ editor }) {
  const active = editor.isActive("heading", { level: 1 }) ? "h1"
               : editor.isActive("heading", { level: 2 }) ? "h2"
               : editor.isActive("heading", { level: 3 }) ? "h3"
               : "p";
  const apply = (val) => {
    if (val === "p") editor.chain().focus().setParagraph().run();
    else editor.chain().focus().toggleHeading({ level: parseInt(val.slice(1), 10) }).run();
  };
  return (
    <select
      value={active}
      onChange={(e) => apply(e.target.value)}
      className="h-7 rounded-sm border border-border bg-white text-xs font-mono px-2 focus:outline-none focus:border-[#0F62FE]"
      data-testid="rte-style-select"
      title="Paragraph style"
    >
      <option value="p">Normal</option>
      <option value="h1">Heading 1</option>
      <option value="h2">Heading 2</option>
      <option value="h3">Heading 3</option>
    </select>
  );
}

const Group = ({ children }) => <div className="flex items-center gap-0.5">{children}</div>;
const Sep = () => <div className="mx-1 h-5 w-px bg-border" />;

function TbBtn({ children, onClick, active, disabled, title, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`h-7 w-7 flex items-center justify-center rounded-sm border ${
        active ? "bg-[#0F62FE] text-white border-[#0F62FE]"
               : "bg-white border-transparent hover:border-border text-foreground"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      {children}
    </button>
  );
}
